import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AgGridTable, Button, PageHeader, Pagination, DateRangePicker, GroupedCheckboxDropdown } from 'components/common';
import { useDispatch, useSelector } from "react-redux";
import dayjs from 'dayjs';
import PropTypes from "prop-types";
import { FaSearch, FaSyncAlt, FaTimes } from "react-icons/fa";
import { FaFileExcel } from "react-icons/fa6";
import { MdFilterAlt } from "react-icons/md";

import { getActiveSalesUnits } from "redux/crm/salesUnitSlice";
import AdvanceFilterDrawer, { getEmptyValues } from "components/common/AdvanceFilterDrawer";
import SalesUnitGroupedDropdown from "components/app/crm/shared/SalesUnitGroupedDropdown";

const THIS_MONTH_START = () => dayjs().startOf("month").format("YYYY-MM-DD");
const TODAY            = () => dayjs().format("YYYY-MM-DD");
const EMPTY_RANGE      = { startDate: "", endDate: "" };

/**
 * Config-driven CRM list page in the Waiver/Contract Tailwind style:
 * gradient header + quick filter bar (search / date ranges / sales unit / selects /
 * advanced drawer) + optional pill tabs + AG Grid + pagination.
 *
 * The active tab's thunk is dispatched with the composed ransack params; the grid
 * reads `reducerSelector(state)[tab.dataKey]` ({ data, totalRows }).
 */
const CrmListPage = ({
  title, subtitle, Icon, gradient,
  reducerSelector, tabs, columns, defaultSort = null,
  searchParamKey, searchPlaceholder,
  dateFilters = [], showSalesUnit = false, salesUnitParamKey = "sales_unit_id_in",
  selects = [], multiSelects = [],
  advancedFields = [], advancedTitle = "Apply filters", advancedDescription = "",
  exportThunk, headerExtra, enableSelection = false, renderSelectionActions,
  perPageDefault = 50, renderCustomTab, reloadSignal
}) => {
  const dispatch = useDispatch();
  const sliceRoot = useSelector(reducerSelector) || {};
  const { exporting } = sliceRoot;
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [view, setView]                 = useState(tabs[0].key);
  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(perPageDefault);
  const [sort, setSort]                 = useState(defaultSort);
  const [search, setSearch]             = useState("");
  const [committedSearch, setCommitted] = useState("");
  const [dateRanges, setDateRanges]     = useState(() =>
    Object.fromEntries(dateFilters.map((f) => [
      f.key,
      f.defaultThisMonth ? { startDate: THIS_MONTH_START(), endDate: TODAY() } : EMPTY_RANGE
    ]))
  );
  const [unitIds, setUnitIds]           = useState([]);
  const [selectValues, setSelectValues] = useState(() => Object.fromEntries(selects.map((s) => [s.key, s.default ?? ""])));
  const [multiSelectValues, setMultiSelectValues] = useState(() => Object.fromEntries(multiSelects.map((m) => [m.key, []])));
  const [filterParams, setFilterParams] = useState({});
  const [filterTags, setFilterTags]     = useState([]);
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(advancedFields));
  const [showFilter, setShowFilter]     = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading]           = useState(false);

  const activeTab = useMemo(() => tabs.find((t) => t.key === view) || tabs[0], [tabs, view]);
  const grid = sliceRoot[activeTab.dataKey] || {};
  const { data = [], totalRows = 0 } = grid;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  useEffect(() => { if (showSalesUnit) dispatch(getActiveSalesUnits()); }, [dispatch, showSalesUnit]);

  // Debounce quick search.
  useEffect(() => {
    const t = setTimeout(() => { setCommitted(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Param builders ────────────────────────────────────────────────────────
  const buildDateParams = useCallback(() => {
    const p = {};
    dateFilters.forEach((f) => {
      const r = dateRanges[f.key] || EMPTY_RANGE;
      if (r.startDate) p[f.paramKeys[0]] = r.startDate;
      if (r.endDate)   p[f.paramKeys[1]] = r.endDate;
    });
    return p;
  }, [dateFilters, dateRanges]);

  const buildSelectParams = useCallback(() => {
    const p = {};
    selects.forEach((s) => { if (selectValues[s.key] !== "" && selectValues[s.key] != null) p[s.paramKey] = selectValues[s.key]; });
    return p;
  }, [selects, selectValues]);

  const buildMultiSelectParams = useCallback(() => {
    const p = {};
    multiSelects.forEach((m) => { const v = multiSelectValues[m.key]; if (Array.isArray(v) && v.length) p[m.paramKey] = v; });
    return p;
  }, [multiSelects, multiSelectValues]);

  const buildListParams = useCallback(() => ({
    page,
    per_page: perPage,
    ...(sort ? { sort } : {}),
    ...(committedSearch ? { [searchParamKey]: committedSearch } : {}),
    ...buildDateParams(),
    ...(showSalesUnit && unitIds.length ? { [salesUnitParamKey]: unitIds } : {}),
    ...buildSelectParams(),
    ...buildMultiSelectParams(),
    ...filterParams
  }), [page, perPage, sort, committedSearch, searchParamKey, buildDateParams, showSalesUnit, unitIds, salesUnitParamKey, buildSelectParams, buildMultiSelectParams, filterParams]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  // Drive the effect off a serialized params signature (not callback identity),
  // so it fires only when the composed query actually changes — never per-render
  // (default array props would otherwise change identity every render → a loop).
  const listParams = buildListParams();
  const paramsSig = JSON.stringify(listParams);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    // Custom tabs (e.g. Kanban) render their own body and fetch their own data.
    if (activeTab.type === "custom" || !activeTab.thunk) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setSelectedRows([]);
    Promise.resolve(dispatch(activeTab.thunk(listParams))).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // paramsSig stands in for listParams and activeTab.key for activeTab (see note above) —
    // depending on the objects themselves would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, paramsSig, activeTab.key, reloadTick, reloadSignal]);

  const isCustomTab = activeTab.type === "custom";

  const triggerReload = useCallback(() => setReloadTick((t) => t + 1), []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSwitchTab = (key) => {
    if (key === view) return;
    setPage(1);
    setView(key);
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    setSort(dir ? { attribute: colId, direction: dir.toUpperCase() } : defaultSort);
    setPage(1);
  }, [defaultSort]);

  const handleDateChange = (key) => (range) => { setDateRanges((p) => ({ ...p, [key]: range })); setPage(1); };
  const handleUnitChange = (ids) => { setUnitIds(ids); setPage(1); };
  const handleSelectChange = (key) => (e) => { setSelectValues((p) => ({ ...p, [key]: e.target.value })); setPage(1); };
  const handleMultiSelectChange = (key) => (ids) => { setMultiSelectValues((p) => ({ ...p, [key]: ids })); setPage(1); };

  const handleApplyFilter = (params, tags) => { setFilterParams(params); setFilterTags(tags); setShowFilter(false); setPage(1); };
  const handleResetFilter = () => { setFilterParams({}); setFilterTags([]); setFilterValues(getEmptyValues(advancedFields)); setPage(1); };
  const removeTag = (tag) => {
    setFilterParams((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== tag.id)));
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
  };

  const handleExport = () => { if (exportThunk) dispatch(exportThunk(listParams)); };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      <PageHeader
        title={title}
        subtitle={subtitle}
        icon={Icon}
        gradient={gradient}
        actions={
          <>
            {enableSelection && selectedRows.length > 0 && renderSelectionActions && renderSelectionActions(selectedRows, triggerReload)}
            {headerExtra}
            <Button variant="secondary" size="sm" onClick={triggerReload} disabled={loading}
              leftIcon={FaSyncAlt} className={loading ? "[&_svg]:tw-animate-spin" : ""}>
              Reload
            </Button>
            {exportThunk && (
              <Button variant="secondary" size="sm" onClick={handleExport} disabled={exporting || loading}
                leftIcon={FaFileExcel} className="tw-text-green-700 tw-border-green-300 hover:tw-bg-green-50">
                {exporting ? "Exporting..." : "Export"}
              </Button>
            )}
          </>
        }
      />

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-56 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={searchPlaceholder}
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400" />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          {(dateFilters.length > 0 || showSalesUnit || selects.length > 0 || multiSelects.length > 0 || advancedFields.length > 0) && (
            <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          )}

          {dateFilters.map((f) => (
            <DateRangePicker key={f.key} label={f.label}
              startDate={dateRanges[f.key]?.startDate || ""} endDate={dateRanges[f.key]?.endDate || ""}
              onChange={handleDateChange(f.key)} />
          ))}

          {selects.map((s) => (
            <select key={s.key} value={selectValues[s.key]} onChange={handleSelectChange(s.key)}
              className="tw-py-1.5 tw-px-2 tw-text-sm tw-border tw-border-slate-200 tw-rounded tw-bg-white tw-text-slate-600 tw-cursor-pointer focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400">
              {s.options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
            </select>
          ))}

          {multiSelects.map((m) => (
            <GroupedCheckboxDropdown
              key={m.key}
              groups={m.groups || [{ key: m.key, label: m.placeholder || m.key, items: (m.options || []).map((o) => ({ value: o.value, label: o.label })) }]}
              selectedIds={multiSelectValues[m.key] || []}
              onChange={handleMultiSelectChange(m.key)}
              placeholder={m.placeholder || "All"}
              showGroupLabels={Boolean(m.groups)}
              width={m.width || 240}
            />
          ))}

          {showSalesUnit && (
            <SalesUnitGroupedDropdown units={activeSalesUnitsData} selectedIds={unitIds} onChange={handleUnitChange} placeholder="All Units" width={220} />
          )}

          {advancedFields.length > 0 && (
            <>
              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
              <button type="button" onClick={() => setShowFilter(true)}
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
                  filterTags.length > 0 ? "tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700"
                    : "tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50"}`}>
                <MdFilterAlt size={15} /> More Filters
                {filterTags.length > 0 && (
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">{filterTags.length}</span>
                )}
              </button>
            </>
          )}

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">{totalRows.toLocaleString()} records</span>
          )}
        </div>

        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(" – ") : String(tag.value)}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-font-medium">
              <FaTimes size={8} /> Clear all
            </button>
          </div>
        )}
      </div>

      {/* Tabs (only when >1) */}
      {tabs.length > 1 && (
        <div className="tw-flex tw-items-center tw-gap-1 tw-border-b tw-border-slate-200">
          {tabs.map(({ key, label, Icon: TabIcon }) => (
            <button key={key} type="button" onClick={() => handleSwitchTab(key)}
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-border-0 tw-border-b-2 tw-bg-transparent tw-cursor-pointer tw-transition-colors -tw-mb-px ${
                view === key ? "tw-border-primary-600 tw-text-primary-700" : "tw-border-transparent tw-text-slate-500 hover:tw-text-slate-700"}`}
              style={{ borderBottomStyle: "solid" }}>
              {TabIcon && <TabIcon size={16} />} {label}
            </button>
          ))}
        </div>
      )}

      {isCustomTab && renderCustomTab ? (
        renderCustomTab({ listParams, loading })
      ) : (
        <>
          {/* Grid */}
          <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
            <AgGridTable
              rowData={data}
              columnDefs={activeTab.columns || columns}
              loading={loading}
              pagination={false}
              filter={false}
              height="max(320px, calc(100dvh - 320px))"
              onSortChanged={handleSort}
              multiSelect={enableSelection}
              onSelectionChanged={enableSelection ? setSelectedRows : undefined}
              enableCellTextSelection
            />
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={perPage}
            pageSizeOptions={[20, 50, 100, 200]}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
            disabled={loading}
          />
        </>
      )}

      {advancedFields.length > 0 && (
        <AdvanceFilterDrawer
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={handleApplyFilter}
          onReset={handleResetFilter}
          fields={advancedFields}
          values={filterValues}
          onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
          title={advancedTitle}
          description={advancedDescription}
        />
      )}
    </div>
  );
};

CrmListPage.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  Icon: PropTypes.elementType,
  gradient: PropTypes.string,
  reducerSelector: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    Icon: PropTypes.elementType,
    type: PropTypes.string,
    thunk: PropTypes.func,
    dataKey: PropTypes.string
  })).isRequired,
  columns: PropTypes.array.isRequired,
  defaultSort: PropTypes.object,
  searchParamKey: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string,
  dateFilters: PropTypes.array,
  showSalesUnit: PropTypes.bool,
  salesUnitParamKey: PropTypes.string,
  selects: PropTypes.array,
  multiSelects: PropTypes.array,
  advancedFields: PropTypes.array,
  advancedTitle: PropTypes.string,
  advancedDescription: PropTypes.string,
  exportThunk: PropTypes.func,
  headerExtra: PropTypes.node,
  enableSelection: PropTypes.bool,
  renderSelectionActions: PropTypes.func,
  perPageDefault: PropTypes.number,
  renderCustomTab: PropTypes.func,
  reloadSignal: PropTypes.any
};

export default CrmListPage;
